import { useEffect, useMemo } from "react";
import { fromJS } from "immutable";
import ReportSidebar from "./components/reports/ReportSidebar";
import ChartView from "./components/reports/ChartView";
import DataTableView from "./components/reports/DataTableView";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "../AppLayout";
import ReportSettingAction from "../actions/ReportSettingAction";
import {
  initialStateLineChart,
  initialStateBarChart,
  initialStateColumnChart,
  initialStatePieChart,
  initialStateScoreCard,
  initialStateGoalChart,
  initialStateTable,
} from "../reducers/reportSettingReducer";
import RateIcon from "../icons/RateIcon";
import MoneyIcon from "../icons/MoneyIcon";
import LeadIcon from "../icons/LeadIcon";
import { getDateUnit } from "./DataUtils";
import { defaultGoal } from "../reducers/goalReducer";
import BucketAction from "../actions/BucketAction";
import LabelAction from "../actions/LabelAction";

export const getChartType = (reportType, reportChartType) => {
  if (!reportType) return undefined as any;
  if (reportType === "chart") {
    switch ((reportChartType || "").toLowerCase()) {
      case "bar":
        return "Bar" as const;
      case "column":
        return "Column" as const;
      case "line":
        return "Line" as const;
      case "pie":
        return "Pie" as const;
      default:
        return undefined as any;
    }
  }
  if (reportType === "scorecard") return "Number" as const;
  if (reportType === "goal") return "Goal" as const;
  if (reportType === "grid") return "Table" as const;
  return undefined as any;
};

export const iconList = [
  { value: "RateIcon", label: "Rate", description: <RateIcon size={20} /> },
  { value: "MoneyIcon", label: "Money", description: <MoneyIcon size={20} /> },
  { value: "LeadIcon", label: "Lead", description: <LeadIcon size={20} /> },
];

const ReportSetting = ({ projectId, dashboardId, reportId, report }) => {
  const dispatch = useDispatch();
  const currentParams = useQuery();

  const reportType = currentParams.get("reportType") || "";
  const reportChartType = currentParams.get("reportChartType") || "";
  const reportObjectType = currentParams.get("reportObjectType") || "SALE";
  const reportViewMode = currentParams.get("reportViewMode") || "";

  const goals = useSelector((state: any) => state.goal.get("goals"));
  const reportData = useSelector((state: any) => state.reportSetting.get("data"));
  const selectedColumns = reportData.get("selectedColumns") || fromJS([]);

  const viewModeEnabled = reportViewMode === "true";
  const chartType = getChartType(reportType, reportChartType);

  const tableColumns = useMemo(() => {
    if (reportObjectType === "SALE") {
      return [
        { key: "name", title: "NAME" },
        { key: "account", title: "ACCOUNT" },
        { key: "assignees", title: "ASSIGNEES" },
        { key: "source", title: "LEAD SOURCE" },
        { key: "bucketId", title: "FUNNEL STAGE" },
        { key: "estimatedRevenue", title: "ESTIMATED REV." },
        { key: "finish", title: "CLOSE DATE" },
        { key: "start", title: "CREATED DATE" },
      ];
    } else if (reportObjectType === "COMPANY") {
      return [
        { key: "name", title: "NAME" },
        { key: "assignees", title: "OWNERS" },
        { key: "labels", title: "LABELS" },
        { key: "bucketId", title: "GROUP" },
        { key: "start", title: "CREATED DATE" },
      ];
    } else if (reportObjectType === "CONTACT") {
      return [
        { key: "name", title: "NAME" },
        { key: "assignees", title: "OWNERS" },
        { key: "labels", title: "LABELS" },
        { key: "bucketId", title: "GROUP" },
        { key: "start", title: "CREATED DATE" },
      ];
    } else if (reportObjectType === "PRODUCT") {
      return [
        { key: "name", title: "NAME" },
        { key: "assignees", title: "OWNERS" },
        { key: "labels", title: "LABELS" },
        { key: "bucketId", title: "GROUP" },
        { key: "start", title: "CREATED DATE" },
      ];
    } else if (reportObjectType === "ACTIVITY") {
      return [
        { key: "done", title: "DONE" },
        { key: "summary", title: "SUMMARY" },
        { key: "assignees", title: "OWNERS" },
        { key: "item", title: "ITEM" },
        { key: "createdAt", title: "CREATED DATE" },
      ];
    }
  }, [reportObjectType]);

  const visibleTableColumns = useMemo(() => {
    const selectedColumnsData = selectedColumns
      .toJS()
      .map((field) => tableColumns.find((item) => item.key === field))
      .filter(Boolean);
    return selectedColumnsData.length > 0 ? selectedColumnsData : tableColumns;
  }, [selectedColumns, tableColumns]);

  useEffect(() => {
    document.title = `Reports`;
  }, []);

  useEffect(() => {
    if (projectId && reportObjectType) {
      dispatch(BucketAction.getBuckets(projectId, reportObjectType));
      dispatch(LabelAction.getLabels(projectId, reportObjectType));
    }
  }, [projectId, reportObjectType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let reportDataSetting: any = {};
    if (chartType === "Line") {
      reportDataSetting = initialStateLineChart;
    } else if (chartType === "Bar") {
      reportDataSetting = initialStateBarChart;
    } else if (chartType === "Column") {
      reportDataSetting = initialStateColumnChart;
    } else if (chartType === "Pie") {
      reportDataSetting = initialStatePieChart;
    } else if (chartType === "Number") {
      reportDataSetting = initialStateScoreCard;
    } else if (chartType === "Goal") {
      reportDataSetting = initialStateGoalChart;
      const goalId = goals.first(fromJS(defaultGoal)).get("id");
      reportDataSetting.goalId = goalId;
      const goal = goals.find((item) => item.get("id") == goalId) || fromJS(defaultGoal);
      reportDataSetting.dateUnit = getDateUnit(goal);
    } else if (chartType === "Table") {
      reportDataSetting = initialStateTable;
    }
    reportDataSetting.selectedColumns = tableColumns.map((item) => item.key);
    if (report && report.get("id")) {
      const currentChartType = getChartType(
        report.get("type"),
        report.getIn(["data", "chartType"])
      );
      if (currentChartType === chartType) {
        const currentReportDataSetting = { ...report.get("data").toJS() };
        if (currentChartType === "Goal") {
          currentReportDataSetting.nameKey = "start";
          currentReportDataSetting.segmentBy = "None";
        }
        dispatch(
          ReportSettingAction.settingDataReport({
            ...reportDataSetting,
            ...currentReportDataSetting,
          })
        );
      } else {
        dispatch(ReportSettingAction.settingDataReport({ ...reportDataSetting }));
      }
    } else {
      dispatch(ReportSettingAction.settingDataReport({ ...reportDataSetting }));
    }
  }, [report, chartType, goals, tableColumns, dispatch]);

  return (
    <div className="p-4 w-full h-full overflow-y-auto min-h-0 bg-[#FAFAFA]">
      <div className="flex gap-4">
        {/* Left Content */}
        <div className="flex-1 space-y-4">
          {!viewModeEnabled || chartType !== "Table" ? (
            <ChartView projectId={projectId} dashboardId={dashboardId} reportId={reportId} />
          ) : null}
          <DataTableView
            tableColumns={visibleTableColumns}
            projectId={projectId}
            objectType={reportObjectType}
          />
        </div>

        {/* Right Sidebar */}
        {!viewModeEnabled ? (
          <ReportSidebar
            projectId={projectId}
            dashboardId={dashboardId}
            reportType={reportType}
            reportChartType={reportChartType}
            reportObjectType={reportObjectType}
            reportViewMode={reportViewMode}
            chartType={chartType}
            tableColumns={tableColumns}
            visibleTableColumns={visibleTableColumns}
          />
        ) : null}
      </div>
    </div>
  );
};

export default ReportSetting;
